import {
    FACET_ETL_DS_TYPE_ESINDEX,
    type FacetFormModel,
    type FacetOp,
    type OpType,
    type TransformEntry,
} from "./facet-form-config";
import { parseAxisSortValue } from "./facet-table-sort-helpers";

function toStringArray(val: unknown): string[] {
    return Array.isArray(val) ? val.filter((v) => typeof v === "string") : [];
}

function transformObjectToEntries(obj: unknown): TransformEntry[] {
    if (obj == null || typeof obj !== "object" || Array.isArray(obj)) return [{ key: "", value: "" }];
    const entries = Object.entries(obj as Record<string, unknown>).map(([k, v]) => ({
        key: k,
        value: typeof v === "string" ? v : String(v ?? ""),
    }));
    return entries.length > 0 ? entries : [{ key: "", value: "" }];
}

export function transformEntriesToObject(entries: TransformEntry[]): Record<string, string> {
    const result: Record<string, string> = {};
    for (const e of entries) {
        if (e.key === "") {
            if (e.value.trim() !== "") result[""] = e.value;
            continue;
        }
        const k = e.key.trim();
        if (k) result[k] = e.value;
    }
    return result;
}

export function isEmptyTransformEditor(entries: TransformEntry[] | undefined): boolean {
    return !(entries ?? []).some((e) => e.key !== "" || e.value.trim() !== "");
}

export function mergeTransformEntryLists(
    committed: TransformEntry[] | undefined,
    draft: TransformEntry[] | undefined
): TransformEntry[] {
    const merged = {
        ...transformEntriesToObject(committed ?? []),
        ...transformEntriesToObject(draft ?? []),
    };
    const entries = Object.entries(merged).map(([key, value]) => ({ key, value }));
    return entries.length > 0 ? entries : [{ key: "", value: "" }];
}

function entriesFromCommittedTransform(committed: TransformEntry[] | undefined): TransformEntry[] {
    const obj = transformEntriesToObject(committed ?? []);
    const entries = Object.entries(obj)
        .filter(([key]) => key !== "")
        .map(([key, value]) => ({ key, value }));
    return entries.length > 0 ? entries : [{ key: "", value: "" }];
}

/** After apply transform, show select values from committed ETL row/col_transform key pairs. */
export function syncPivotTransformEditorsFromEtl(
    model: FacetFormModel,
    etlJson: string
): FacetFormModel {
    const committed = parseJsonToFormModel(etlJson);
    if (!committed) return model;

    return {
        ...model,
        ops: model.ops.map((op, i) => {
            if (op.type !== "pivot") return op;
            const committedOp = committed.ops[i]?.type === "pivot" ? committed.ops[i] : null;
            return {
                ...op,
                row_transform: entriesFromCommittedTransform(committedOp?.row_transform),
                col_transform: entriesFromCommittedTransform(committedOp?.col_transform),
            };
        }),
    };
}

export function serializeFormModelWithCommittedTransforms(
    model: FacetFormModel,
    committedJson: string,
    notebookId?: number
): string {
    const committed = parseJsonToFormModel(committedJson);
    const mergedOps = model.ops.map((op, i) => {
        if (op.type !== "pivot") return op;
        const committedOp = committed?.ops[i]?.type === "pivot" ? committed.ops[i] : null;
        return {
            ...op,
            row_transform: mergeTransformEntryLists(
                committedOp?.row_transform,
                op.row_transform
            ),
            col_transform: mergeTransformEntryLists(
                committedOp?.col_transform,
                op.col_transform
            ),
        };
    });
    return serializeFormModelToJson({ ...model, ops: mergedOps }, notebookId);
}

type TransformApplyAxis = "row" | "col";

export function serializeFormModelForTransformApply(
    model: FacetFormModel,
    committedJson: string,
    applyTarget: { opIndex: number; axis: TransformApplyAxis },
    notebookId?: number
): string {
    const committed = parseJsonToFormModel(committedJson);
    const mergedOps = model.ops.map((op, i) => {
        if (op.type !== "pivot") return op;
        const committedOp = committed?.ops[i]?.type === "pivot" ? committed.ops[i] : null;
        const clearRow =
            i === applyTarget.opIndex &&
            applyTarget.axis === "row" &&
            isEmptyTransformEditor(op.row_transform);
        const clearCol =
            i === applyTarget.opIndex &&
            applyTarget.axis === "col" &&
            isEmptyTransformEditor(op.col_transform);
        return {
            ...op,
            row_transform: clearRow
                ? [{ key: "", value: "" }]
                : mergeTransformEntryLists(committedOp?.row_transform, op.row_transform),
            col_transform: clearCol
                ? [{ key: "", value: "" }]
                : mergeTransformEntryLists(committedOp?.col_transform, op.col_transform),
        };
    });
    return serializeFormModelToJson({ ...model, ops: mergedOps }, notebookId);
}

function parseOpFromRaw(raw: Record<string, unknown>): FacetOp | null {
    const rawType = typeof raw.type === "string" ? raw.type : "";
    if (rawType === "normalize") {
        const legacyValue = typeof raw.value === "string" ? raw.value.trim() : "";
        const legacyParty = typeof raw.reference_key === "string" ? raw.reference_key.trim() : "";
        let transform = transformObjectToEntries(raw.transform);
        if (legacyValue && legacyParty && isEmptyTransformEditor(transform)) {
            transform = [{ key: legacyValue, value: legacyParty }];
        }
        return {
            type: "normalize",
            key: (raw.key as string) ?? "",
            reference_table:
                typeof raw.reference_table === "string" ? raw.reference_table : "",
            transform,
        };
    }

    const type: OpType = rawType === "pivot" || rawType === "matrix" ? "pivot" : "filter";
    const op: FacetOp = {
        type,
        mode: (raw.mode as string) ?? "blacklist",
        runon: (raw.runon as string) ?? "esnode",
        "data-type": (raw["data-type"] as string) ?? "str",
    };

    if (type === "filter") {
        op.key = (raw.key as string) ?? "";
        op.blacklist = toStringArray(raw.blacklist);
        op.whitelist = toStringArray(raw.whitelist);
    }

    if (type === "pivot") {
        op.col_key = (raw.col_key as string) ?? "";
        op.row_key = (raw.row_key as string) ?? "";
        op.sort_by = (raw.sort_by as string) ?? "value";
        op.sort_order = (raw.sort_order as string) ?? "desc";
        op.row_transform = transformObjectToEntries(raw.row_transform ?? raw.transform);
        op.col_transform = transformObjectToEntries(raw.col_transform);
        op.col_blacklist = toStringArray(raw.col_blacklist);
        op.col_whitelist = toStringArray(raw.col_whitelist);
        op.row_blacklist = toStringArray(raw.row_blacklist);
        op.row_whitelist = toStringArray(raw.row_whitelist);
        op.include_total_cols = raw.include_total_cols === true;
        op.include_total_rows = raw.include_total_rows === true;
        op.row_sort = parseAxisSortValue(raw.row_sort);
        op.column_sort = parseAxisSortValue(raw.column_sort ?? raw.col_sort);
    }

    return op;
}

export function parseJsonToFormModel(json: string): FacetFormModel | null {
    try {
        const root = JSON.parse(json || "{}") as Record<string, unknown>;
        const etl = root.etl as Record<string, unknown> | undefined;
        if (!etl || typeof etl !== "object") return null;

        const rawOps = Array.isArray(etl.ops) ? etl.ops : [];
        return {
            name: typeof etl.name === "string" ? etl.name : "",
            decimal_places: typeof root.decimal_places === "number" ? root.decimal_places : 6,
            ds: (etl.ds as Record<string, unknown>) ?? {},
            ops: rawOps
                .map((o: unknown) => parseOpFromRaw((o ?? {}) as Record<string, unknown>))
                .filter((op): op is FacetOp => op != null),
        };
    } catch {
        return null;
    }
}

function serializeOp(op: FacetOp): Record<string, unknown> {
    if (op.type === "normalize") {
        const normalizeOp: Record<string, unknown> = {
            type: "normalize",
            key: op.key ?? "",
        };
        const referenceTable = op.reference_table?.trim();
        if (referenceTable) normalizeOp.reference_table = referenceTable;
        const transform = transformEntriesToObject(op.transform ?? []);
        if (Object.keys(transform).length > 0) normalizeOp.transform = transform;
        return normalizeOp;
    }

    const base: Record<string, unknown> = {
        type: op.type,
        mode: op.mode,
        runon: op.runon,
        "data-type": op["data-type"],
    };

    if (op.type === "filter") {
        base.key = op.key ?? "";
        base.blacklist = op.blacklist ?? [];
        base.whitelist = op.whitelist ?? [];
    }

    if (op.type === "pivot") {
        base.col_key = op.col_key ?? "";
        base.row_key = op.row_key ?? "";
        base.sort_by = op.sort_by ?? "value";
        base.sort_order = op.sort_order ?? "desc";
        base.row_transform = transformEntriesToObject(op.row_transform ?? []);
        base.col_transform = transformEntriesToObject(op.col_transform ?? []);
        base.col_blacklist = op.col_blacklist ?? [];
        base.col_whitelist = op.col_whitelist ?? [];
        base.row_blacklist = op.row_blacklist ?? [];
        base.row_whitelist = op.row_whitelist ?? [];
        base.include_total_cols = op.include_total_cols ?? true;
        base.include_total_rows = op.include_total_rows ?? true;
        base.row_sort = Array.isArray(op.row_sort) ? op.row_sort : (op.row_sort ?? {});
        base.column_sort = Array.isArray(op.column_sort)
            ? op.column_sort
            : (op.column_sort ?? {});
    }

    return base;
}

export function serializeFormModelToJson(model: FacetFormModel, notebookId?: number): string {
    const ds: Record<string, unknown> = { ...model.ds };
    ds.type = FACET_ETL_DS_TYPE_ESINDEX;
    const rawName = ds.esindex_name;
    ds.esindex_name =
        typeof rawName === "string" ? rawName.trim() : rawName != null ? String(rawName).trim() : "";
    if (typeof notebookId === "number" && Number.isFinite(notebookId) && notebookId > 0) {
        ds.notebook_id = notebookId;
    }

    const root = {
        etl: {
            ds,
            ops: model.ops.map(serializeOp),
            name: model.name,
        },
        decimal_places: model.decimal_places,
    };
    return JSON.stringify(root, null, 2);
}

export function defaultOpForType(type: OpType): FacetOp {
    if (type === "normalize") {
        return { type: "normalize", key: "", reference_table: "", transform: [{ key: "", value: "" }] };
    }

    const base: FacetOp = {
        type,
        mode: "blacklist",
        runon: "esnode",
        "data-type": "str",
    };

    if (type === "filter") {
        base.key = "";
        base.blacklist = [];
        base.whitelist = [];
    }

    if (type === "pivot") {
        base.col_key = "";
        base.row_key = "";
        base.sort_by = "value";
        base.sort_order = "desc";
        base.row_transform = [{ key: "", value: "" }];
        base.col_transform = [{ key: "", value: "" }];
        base.col_blacklist = [];
        base.col_whitelist = [];
        base.row_blacklist = [];
        base.row_whitelist = [];
        base.include_total_cols = true;
        base.include_total_rows = true;
        base.row_sort = {};
        base.column_sort = {};
    }

    return base;
}

export interface FacetFormValidationErrors {
    name?: string;
    decimal_places?: string;
    ops?: Record<number, Record<string, string>>;
}

export function validateFormModel(model: FacetFormModel): FacetFormValidationErrors | null {
    const errors: FacetFormValidationErrors = {};
    let hasError = false;

    if (!model.name.trim()) {
        errors.name = "Name is required";
        hasError = true;
    }

    if (!Number.isFinite(model.decimal_places) || model.decimal_places < 0 || model.decimal_places > 20) {
        errors.decimal_places = "Must be 0–20";
        hasError = true;
    }

    const opsErrors: Record<number, Record<string, string>> = {};
    model.ops.forEach((op, i) => {
        const opErr: Record<string, string> = {};
        if (op.type === "filter" && !op.key?.trim()) {
            opErr.key = "Key is required for filter";
        }
        if (op.type === "pivot" && !op.row_key?.trim()) {
            opErr.row_key = "Row key is required";
        }
        if (op.type === "normalize" && !op.key?.trim()) {
            opErr.key = "Key is required for normalize";
        }
        if (Object.keys(opErr).length > 0) {
            opsErrors[i] = opErr;
            hasError = true;
        }
    });

    if (Object.keys(opsErrors).length > 0) errors.ops = opsErrors;
    return hasError ? errors : null;
}
